import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const Modal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = 'medium',
  showCloseButton = true,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeClasses = {
    small: 'max-w-sm',
    medium: 'max-w-lg',
    large: 'max-w-3xl',
    full: 'max-w-[95vw]',
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return createPortal(
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-lg animate-fade-in"
      onClick={handleBackdropClick}
    >
      <div 
        className={`${sizeClasses[size] || sizeClasses.medium} w-full bg-surface-raised border border-surface-border/60 rounded-lg flex flex-col max-h-[85vh] shadow-glow-lg animate-appear overflow-hidden`}
      >
        {/* Accent top strip */}
        <div className="h-[2px] bg-gradient-to-r from-transparent via-accent/60 to-transparent shrink-0" />

        {/* Header */}
        <div className="flex justify-between items-center px-5 py-3 shrink-0">
          <div>
            {title && (
              <h2 className="text-sm font-semibold text-text-primary uppercase tracking-wide">
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="text-[11px] text-text-muted mt-0.5">
                {subtitle}
              </p>
            )}
          </div>
          {showCloseButton && (
            <button
              onClick={onClose}
              className="p-1 text-text-muted hover:text-accent rounded transition-colors cursor-pointer"
              aria-label="Close modal"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Divider */}
        <div className="h-px bg-surface-border/50 mx-5 shrink-0" />

        {/* Content */}
        <div className="px-5 py-4 overflow-y-auto flex-1 min-h-0">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="px-5 py-3 border-t border-surface-border/50 bg-surface-overlay/30 flex justify-end gap-2 shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
